/**
 * <PolicyEditor> — how much of the support pipeline runs on its own.
 *
 * Every human gate in the lifecycle (plan approval, QC, PM approval, release)
 * can be left to a person or delegated to the agent for LOW-risk changes; the
 * risk itself is always computed by the server from the plan, never taken
 * from the agent's word, and a high-risk change always waits for a person.
 * This form edits the project's stored overrides — anything left alone keeps
 * following the platform default, so an improved default still reaches it.
 *
 * The vocabularies (issue types, severities, priorities) are edited elsewhere
 * in a later phase; this screen is the autonomy settings a project manager
 * actually changes.
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { Bot, FlaskConical, Github, Mail, Rocket, Save, Settings2 } from 'lucide-react'

import type { SupportMeta, SupportNotify, SupportPolicyDoc, SupportRuntime } from './contract'
import { gateOptions, githubModeOptions, releaseModeOptions } from './options'
import {
  Card, Empty, ErrorLine, Spinner, SupportScope, btn, errorText, inputStyle, labelStyle, mutedText,
} from './ui'

export interface PolicyEditorProps {
  runtime: SupportRuntime
  meta: SupportMeta
  notify?: SupportNotify
}

type Section = 'gates' | 'build' | 'from_tests' | 'release' | 'github' | 'notifications'

function Field({ label, help, children }: { label: string; help?: string; children: ReactNode }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label style={labelStyle}>{label}</label>
      {children}
      {help && <div style={{ ...mutedText, marginTop: 3 }}>{help}</div>}
    </div>
  )
}

function Toggle({ checked, onChange, label, disabled }: {
  checked: boolean; onChange: (v: boolean) => void; label: string; disabled?: boolean
}) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, cursor: disabled ? 'default' : 'pointer' }}>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span>{label}</span>
    </label>
  )
}

export default function PolicyEditor({ runtime, meta, notify }: PolicyEditorProps) {
  const [policy, setPolicy] = useState<SupportPolicyDoc | null>(null)
  const [dirty, setDirty] = useState<Set<Section>>(new Set())
  const [error, setError] = useState('')
  const [saveError, setSaveError] = useState('')
  const [busy, setBusy] = useState(false)
  const canEdit = meta.me.capacities.includes('pm') && !!runtime.putPolicy

  const load = useCallback(async () => {
    if (!runtime.getPolicy) return
    try {
      const res = await runtime.getPolicy()
      setPolicy(res.policy)
      setDirty(new Set())
      setError('')
    } catch (e) {
      setError(errorText(e, 'The support settings could not be loaded.'))
    }
  }, [runtime])

  useEffect(() => { void load() }, [load])

  if (!runtime.getPolicy) {
    return <SupportScope><Empty>Support settings are managed on the platform.</Empty></SupportScope>
  }

  // build.ui_tests is one nested object; edit it a key at a time.
  const setUi = (key: 'mode' | 'max_cases' | 'blocking', value: unknown) => {
    const cur = ((policy?.build as Record<string, unknown> | undefined)?.ui_tests || {}) as Record<string, unknown>
    set('build', 'ui_tests', { ...cur, [key]: value })
  }
  // from_tests.gates is one nested object too.
  const setFtGate = (key: 'plan_approval' | 'qc', value: string) => {
    const cur = ((policy?.from_tests as Record<string, unknown> | undefined)?.gates || {}) as Record<string, unknown>
    set('from_tests', 'gates', { ...cur, [key]: value })
  }
  const set = <S extends Section>(section: S, key: string, value: unknown) => {
    setPolicy((cur) => {
      if (!cur) return cur
      const sec = { ...((cur[section] as Record<string, unknown>) || {}), [key]: value }
      return { ...cur, [section]: sec }
    })
    setDirty((cur) => new Set(cur).add(section))
  }

  const save = async () => {
    if (!policy || !runtime.putPolicy) return
    setBusy(true)
    setSaveError('')
    try {
      // Only the sections that changed are sent, so an untouched section
      // keeps following the platform default.
      const patch: SupportPolicyDoc = {}
      dirty.forEach((s) => { patch[s] = policy[s] })
      const res = await runtime.putPolicy(patch)
      setPolicy(res.policy)
      setDirty(new Set())
      notify?.success('Support settings saved.')
    } catch (e) {
      setSaveError(errorText(e, 'The settings could not be saved.'))
    } finally {
      setBusy(false)
    }
  }

  const g = policy?.gates || {}
  const b = policy?.build || {}
  const ui = b.ui_tests || {}
  const ft = policy?.from_tests || {}
  const ftg = ft.gates || {}
  const r = policy?.release || {}
  const gh = policy?.github || {}
  const n = policy?.notifications || {}
  const dis = !canEdit

  return (
    <SupportScope>
      <ErrorLine message={error} onRetry={() => { void load() }} />
      {!policy && !error && <div style={{ display: 'flex', gap: 8 }}><Spinner /> Loading…</div>}
      {policy && (
        <>
          {!canEdit && (
            <div style={{ ...mutedText, marginBottom: 10 }}>Only a project manager can change these settings.</div>
          )}
          <Card title="Gates and autonomy" icon={<Bot size={15} />}>
            <Toggle checked={g.auto_triage !== false} disabled={dis} onChange={(v) => set('gates', 'auto_triage', v)}
              label="Triage every new report automatically (type, severity, duplicates, questions for the reporter)" />
            <Field label="Analyse automatically"
              help="By type: bugs and performance issues are analysed straight away; feature requests wait for a person to accept them.">
              <select value={g.auto_analyse || 'by_type'} disabled={dis} style={inputStyle}
                onChange={(e) => set('gates', 'auto_analyse', e.target.value)}>
                {gateOptions(meta, 'auto_analyse').map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
            </Field>
            <Field label="Plan approval before building">
              <select value={g.plan_approval || 'human'} disabled={dis} style={inputStyle}
                onChange={(e) => set('gates', 'plan_approval', e.target.value)}>
                {gateOptions(meta, 'plan_approval').map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
            </Field>
            <Field label="Confidence needed for an automatic plan approval"
              help="0 to 1 — how sure the AI must be of the root cause.">
              <input type="number" min={0} max={1} step={0.05} disabled={dis || !g.plan_approval || g.plan_approval === 'human'}
                value={typeof g.auto_confidence_threshold === 'number' ? g.auto_confidence_threshold : 0.85}
                onChange={(e) => set('gates', 'auto_confidence_threshold', Math.min(1, Math.max(0, Number(e.target.value) || 0)))}
                style={{ ...inputStyle, width: 120 }} />
            </Field>
            <Field label="Quality control">
              <select value={g.qc || 'human'} disabled={dis} style={inputStyle}
                onChange={(e) => set('gates', 'qc', e.target.value)}>
                {gateOptions(meta, 'qc').map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
            </Field>
            <Field label="Project manager approval">
              <select value={g.pm_approval || 'human'} disabled={dis} style={inputStyle}
                onChange={(e) => set('gates', 'pm_approval', e.target.value)}>
                {gateOptions(meta, 'pm_approval').map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
            </Field>
            <div style={mutedText}>A high-risk change — for example one that drops or renames a column — always waits for a person.</div>
          </Card>

          <Card title="Building fixes" icon={<Settings2 size={15} />}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <Field label="Automatic correction cycles" help="Times the AI may fix its own failing tests before a developer is asked.">
                <input type="number" min={0} max={5} disabled={dis} style={inputStyle}
                  value={typeof b.max_correction_cycles === 'number' ? b.max_correction_cycles : 2}
                  onChange={(e) => set('build', 'max_correction_cycles', Math.max(0, parseInt(e.target.value, 10) || 0))} />
              </Field>
              <Field label="Builds at the same time">
                <input type="number" min={1} max={10} disabled={dis} style={inputStyle}
                  value={typeof b.max_concurrent_builds === 'number' ? b.max_concurrent_builds : 2}
                  onChange={(e) => set('build', 'max_concurrent_builds', Math.max(1, parseInt(e.target.value, 10) || 1))} />
              </Field>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
              <Field label="Browser tests on staging"
                help="The application's UI acceptance cases, run in a real browser on each fix's staging copy — always including the case that reported the issue.">
                <select value={ui.mode || 'touched'} disabled={dis} style={inputStyle}
                  onChange={(e) => setUi('mode', e.target.value)}>
                  <option value="touched">Cases on the objects the fix changed</option>
                  <option value="all">Every case</option>
                  <option value="off">Off</option>
                </select>
              </Field>
              <Field label="Most cases per fix">
                <input type="number" min={1} max={500} disabled={dis || ui.mode === 'off'} style={inputStyle}
                  value={typeof ui.max_cases === 'number' ? ui.max_cases : 40}
                  onChange={(e) => setUi('max_cases', Math.min(500, Math.max(1, parseInt(e.target.value, 10) || 1)))} />
              </Field>
            </div>
            <Toggle checked={ui.blocking !== false} disabled={dis || ui.mode === 'off'}
              onChange={(v) => setUi('blocking', v)}
              label="A failing browser test sends the fix back for correction" />
          </Card>

          <Card title="Failed acceptance tests" icon={<FlaskConical size={15} />}>
            <Toggle checked={ft.auto_send !== false} disabled={dis}
              onChange={(v) => set('from_tests', 'auto_send', v)}
              label="Send a failure that is obviously an application error to Support automatically" />
            <div style={mutedText}>
              Obvious means the test triage called it an application bug, the screen or a visual showed an error,
              the server answered with an error, or the page crashed. Everything else waits on the Testing screen
              for a person to decide — with guidance Support then works from.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
              <Field label="A failure seen in this many runs needs a person"
                help="0 = never, however often it fails.">
                <input type="number" min={0} max={50} disabled={dis} style={inputStyle}
                  value={typeof ft.repeat_threshold === 'number' ? ft.repeat_threshold : 3}
                  onChange={(e) => set('from_tests', 'repeat_threshold', Math.max(0, parseInt(e.target.value, 10) || 0))} />
              </Field>
              <Field label="Automatic tries per test case"
                help="After this many automatic sends, the next failure of the same case goes to a person.">
                <input type="number" min={0} max={10} disabled={dis} style={inputStyle}
                  value={typeof ft.max_auto_sends === 'number' ? ft.max_auto_sends : 1}
                  onChange={(e) => set('from_tests', 'max_auto_sends', Math.max(0, parseInt(e.target.value, 10) || 0))} />
              </Field>
            </div>
            <Toggle checked={ft.browser_runs !== 'same'} disabled={dis}
              onChange={(v) => set('from_tests', 'browser_runs', v ? 'person' : 'same')}
              label="A failure found in someone's own browser (a run driven from Claude Code) is always decided by a person" />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
              <Field label="Plan approval for test-found issues">
                <select value={ftg.plan_approval || 'auto_low_risk'} disabled={dis} style={inputStyle}
                  onChange={(e) => setFtGate('plan_approval', e.target.value)}>
                  {gateOptions(meta, 'plan_approval').map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                </select>
              </Field>
              <Field label="Quality control for test-found issues">
                <select value={ftg.qc || 'agent_low_risk'} disabled={dis} style={inputStyle}
                  onChange={(e) => setFtGate('qc', e.target.value)}>
                  {gateOptions(meta, 'qc').map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                </select>
              </Field>
            </div>
            <div style={mutedText}>Releasing still follows the project manager approval above.</div>
          </Card>

          <Card title="Releases" icon={<Rocket size={15} />}>
            <Field label="How releases are cut">
              <select value={r.mode || 'pm_bundle'} disabled={dis} style={inputStyle}
                onChange={(e) => set('release', 'mode', e.target.value)}>
                {releaseModeOptions(meta).map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
            </Field>
            <Toggle checked={r.notify_reporters !== false} disabled={dis}
              onChange={(v) => set('release', 'notify_reporters', v)}
              label="Tell reporters when their issue is released" />
          </Card>

          <Card title="GitHub" icon={<Github size={15} />}>
            <Field label="Publishing a release"
              help="Pull request: open a release PR, merge it through the API, then tag — required where the branch is protected.">
              <select value={gh.mode || 'auto'} disabled={dis} style={inputStyle}
                onChange={(e) => set('github', 'mode', e.target.value)}>
                {githubModeOptions(meta).map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
            </Field>
            <Field label="Target branch" help="Empty = the repository's own branch.">
              <input value={gh.target_branch || ''} disabled={dis} style={inputStyle} placeholder="main"
                onChange={(e) => set('github', 'target_branch', e.target.value.trim())} />
            </Field>
            <Toggle checked={gh.create_release !== false} disabled={dis}
              onChange={(v) => set('github', 'create_release', v)}
              label="Create a GitHub Release with the release notes" />
          </Card>

          <Card title="E-mail" icon={<Mail size={15} />}>
            <Toggle checked={n.email_reporter !== false} disabled={dis}
              onChange={(v) => set('notifications', 'email_reporter', v)}
              label="E-mail reporters about their issues" />
            <Toggle checked={n.email_func_admins !== false} disabled={dis}
              onChange={(v) => set('notifications', 'email_func_admins', v)}
              label="E-mail each organization's administrators when a release is available" />
          </Card>

          <ErrorLine message={saveError} />
          {canEdit && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" style={btn('ghost')} disabled={busy || !dirty.size} onClick={() => { void load() }}>
                Discard changes
              </button>
              <button type="button" style={btn('primary')} disabled={busy || !dirty.size} onClick={() => { void save() }}>
                {busy ? <Spinner size={13} /> : <Save size={13} />} Save settings
              </button>
            </div>
          )}
        </>
      )}
    </SupportScope>
  )
}
